import type { AppContextType } from "@1js/acui-context";
import type {
  BuildTimeControlWidths,
  RibbonScalingStateSLRWithCacheKeys,
} from "..";
import {
  estimateRibbonScalingStepsSLR,
  getRibbonBottomBarWidthSLR,
  initializeVirtualScalingStateSLR,
  scaleRibbonDown,
} from "..";
import { setEstimatedScalingWidths } from "../estimatedWidths";
import {
  chevronWidth,
  tabContentContainerPadding,
} from "../RibbonBottomBarConstants";
import { vNextRibbonModeSwitcherLeftRightMargin } from "../../../ribbonStylingConstantsBase";

/**
 * Determines the scalingState to be rendered given the buildTimeControlWidths
 */
export function scaleRibbonDownWithBuildTimeScalingSLR(
  scalingState: RibbonScalingStateSLRWithCacheKeys,
  knownWidths: BuildTimeControlWidths,
  reactMLRIsEnabled: boolean,
  reservedSpace: number,
  onWarning: ((message: string) => void) | undefined,
  appContext: AppContextType
) {
  // Initialize the virtualScalingState and fill it with our known widths
  const draftVirtualScalingState = initializeVirtualScalingStateSLR(
    scalingState,
    knownWidths
  );

  // Determine the width of the ribbon
  const scalingData = getRibbonBottomBarWidthSLR(
    scalingState,
    knownWidths,
    tabContentContainerPadding,
    reactMLRIsEnabled
  );

  // Log errors on controls that we tried to estimate but couldn't
  scalingData.unknownIds.forEach((controlId) => {
    onWarning?.(`Trying to estimate unknown width. ControlId: ${controlId}`);
  });

  // Determine the effect of applying each scaling step
  const estimatedWidths = estimateRibbonScalingStepsSLR(
    scalingState,
    scalingData.width,
    appContext,
    draftVirtualScalingState
  );

  // Set the calculated estimatedWidths so they don't have to be re-computed by the quickScaling logic
  setEstimatedScalingWidths(estimatedWidths);

  const spaceAvailable =
    window.innerWidth -
    (reactMLRIsEnabled ? chevronWidth : 0) -
    reservedSpace -
    // Next style adds 4px of margin on each side of ribbon mode button
    (reactMLRIsEnabled ? vNextRibbonModeSwitcherLeftRightMargin * 2 : 0);

  // If the entire ribbon content fits, don't apply any scaling steps
  if (spaceAvailable > scalingData.width) {
    return scalingState;
  }

  // Determine the scaling step that first allows content to fit in the window
  for (const step in estimatedWidths) {
    if ((estimatedWidths[step] as number) < spaceAvailable) {
      return scaleRibbonDown(
        scalingState,
        parseInt(step, 10) - scalingState.appliedScalingStepCount
      );
    }
  }

  return scaleRibbonDown(
    scalingState,
    scalingState.scalingSteps.length - scalingState.appliedScalingStepCount
  );
}
